import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { AirtableStatus } from 'gatsby-theme-hem-store/src/constants';
import { useShowPreOrderVariants } from 'gatsby-theme-hem-store/src/hooks/useShowPreOrderVariants';
import isArray from 'lodash/isArray';
import toNumber from 'lodash/toNumber';
import uniq from 'lodash/uniq';
import React from 'react';

import { useIsHemX } from '@/hooks/useIsHemX';
import { useIsSample } from '@/hooks/useIsSample';

import { GatsbyContentfulImage } from '../GatsbyContentfulImage';
import { useMoneyString } from '../Money';
import { Variant } from '../next/variant';

interface Props extends Queries.VariantGridItemFragment {
  onClick?: () => void;
  thumbnailImageUrl?: string;
  hoverImageUrl?: string;
}

export const VariantGridItem: React.FC<Props> = ({
  sku,
  slug,
  productName,
  variantName,
  thumbnailImage,
  hoverImage,
  thumbnailImageUrl,
  hoverImageUrl,
  shopify,
  airtable,
  onClick,
}) => {
  const showPreOrderVariants = useShowPreOrderVariants();
  const { isHemX } = useIsHemX(slug);
  const { isSample } = useIsSample(slug);
  const isPreOrder = airtable.status === AirtableStatus.PRE_ORDER;
  const price = toNumber(shopify.price);
  const priceString = useMoneyString({ amount: price, sku });
  const compareAtPrice = toNumber(shopify.compareAtPrice);
  const compareAtPriceString = useMoneyString({ amount: compareAtPrice, sku });

  if (!showPreOrderVariants && isPreOrder) {
    return null;
  }

  const colors = uniq(
    [
      airtable.colorHex,
      ...(isArray(airtable?.product?.colorsHex)
        ? airtable.product.colorsHex
        : []),
    ].filter(Boolean),
  );

  const isSoldOut =
    shopify.inventoryQuantity < 1 &&
    airtable.status !== AirtableStatus.ACTIVE &&
    airtable.status !== AirtableStatus.PRE_ORDER;

  return (
    <Variant
      isSoldOut={isSoldOut}
      onClick={onClick}
      familyName={productName}
      href={slug}
      isPreOrder={isPreOrder}
      image={
        thumbnailImageUrl ? (
          <GatsbyContentfulImage
            imageUrl={thumbnailImageUrl}
            alt={`${productName} ${variantName}`}
          />
        ) : (
          <GatsbyImage
            image={thumbnailImage.gatsbyImageData}
            alt={`${productName} ${variantName}`}
          />
        )
      }
      hoverImage={
        hoverImageUrl ? (
          <GatsbyContentfulImage
            imageUrl={hoverImageUrl}
            alt={`${productName} ${variantName}`}
          />
        ) : (
          <GatsbyImage
            image={hoverImage.gatsbyImageData}
            alt={`${productName} ${variantName}`}
          />
        )
      }
      price={priceString}
      compareAtPrice={compareAtPrice > price ? compareAtPriceString : null}
      discountPercentage={
        compareAtPrice > price
          ? Math.round((1 - price / compareAtPrice) * 100)
          : null
      }
      colors={colors}
      className="col-span-3"
      productConfig={undefined}
      variant={variantName}
      isHemX={isHemX}
      isSample={isSample}
    />
  );
};

export const query = graphql`
  fragment VariantGridItem on ContentfulProductVariant {
    sku
    slug
    productName
    variantName
    thumbnailImage {
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        width: 460
        height: 460
        placeholder: NONE
        resizingBehavior: PAD
      )
    }
    hoverImage {
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        width: 460
        height: 460
        placeholder: NONE
        resizingBehavior: PAD
      )
    }
    airtable {
      colorHex
      status
      type
      product {
        colorsHex
      }
    }
    shopify {
      price
      compareAtPrice
      inventoryQuantity
    }
  }
`;
